import { User } from 'firebase/auth';

// Mock authentication for local development
interface MockUserData {
  password: string;
  role: string;
  uid: string;
  email: string;
  displayName: string;
}

const mockUsers: Record<string, MockUserData> = {
  'admin@familytree.com': {
    password: 'Family@2024',
    role: 'admin',
    uid: 'admin-uid-123',
    email: 'admin@familytree.com',
    displayName: 'Admin User'
  },
  'guest@familytree.com': {
    password: 'Member@2024',
    role: 'guest',
    uid: 'guest-uid-456',
    email: 'guest@familytree.com',
    displayName: 'Guest User'
  }
};

// Mock User class that implements enough User properties to work with our app
export class MockUser implements Partial<User> {
  uid: string;
  email: string | null;
  displayName: string | null;
  emailVerified: boolean;
  isAnonymous: boolean;
  metadata: { creationTime?: string; lastSignInTime?: string };
  providerData: Array<any>;
  refreshToken: string;

  constructor(uid: string, email: string | null, displayName: string | null) {
    this.uid = uid;
    this.email = email;
    this.displayName = displayName;
    this.emailVerified = true;
    this.isAnonymous = false;
    this.metadata = { 
      creationTime: new Date().toISOString(),
      lastSignInTime: new Date().toISOString()
    };
    this.providerData = [];
    this.refreshToken = '';
  }
}

// Mock Authentication functions
export const loginUser = async (email: string, password: string): Promise<User> => {
  // Small delay to simulate network request
  await new Promise(resolve => setTimeout(resolve, 500));
  
  if (!mockUsers[email]) {
    throw new Error('Invalid email or password');
  }
  
  const userInfo = mockUsers[email];
  
  if (userInfo.password !== password) {
    throw new Error('Invalid email or password');
  }
  
  const user = new MockUser(userInfo.uid, userInfo.email, userInfo.displayName) as User;
  
  // Store current user in localStorage for session persistence
  localStorage.setItem('currentUser', JSON.stringify(user));
  return user;
};

export const logoutUser = async (): Promise<boolean> => {
  // Small delay to simulate network request
  await new Promise(resolve => setTimeout(resolve, 300));
  localStorage.removeItem('currentUser');
  return true;
};

// Adding back registerUser function for setupAccounts.ts
export const registerUser = async (email: string, password: string, role: string = 'guest'): Promise<User> => {
  // Small delay to simulate network request
  await new Promise(resolve => setTimeout(resolve, 700));
  
  // Create a new mock user
  const uid = `user-${Math.random().toString(36).substring(2, 9)}`;
  const user = new MockUser(uid, email, email.split('@')[0]) as User;
  
  // Add to mockUsers
  mockUsers[email] = {
    password,
    role,
    uid,
    email,
    displayName: email.split('@')[0]
  };
  
  return user;
};

export const getUserRole = async (user: Partial<User>): Promise<string | null> => {
  // Small delay to simulate network request
  await new Promise(resolve => setTimeout(resolve, 300));
  
  const email = user.email;
  if (!email || !mockUsers[email]) {
    return 'guest';
  }
  
  return mockUsers[email].role;
};

// Mock auth object to replace Firebase auth
export const auth = {
  currentUser: null as User | null,
  
  // Mock onAuthStateChanged to simulate Firebase behavior
  onAuthStateChanged: (callback: (user: User | null) => void) => {
    // Get user from localStorage if exists
    const storedUser = localStorage.getItem('currentUser');
    const user = storedUser ? JSON.parse(storedUser) as User : null;
    
    // Call the callback with the current user
    setTimeout(() => {
      callback(user);
    }, 0);
    
    // Return unsubscribe function
    return () => {};
  }
};

// Mock database
const mockDb: { familyData: Record<string, any> } = {
  familyData: {}
};

export const saveFamilyData = async (userId: string, familyData: any): Promise<boolean> => {
  mockDb.familyData[userId] = familyData;
  return true;
};

export const getFamilyData = async (userId: string): Promise<any> => {
  return mockDb.familyData[userId] || null;
};

// Export mock db for consistency with original code
export const db = {};
